import React from "react";
import { ContactType } from "@akfdev-com/shared";
import {
  MagnifyingGlassIcon,
  Bars3BottomLeftIcon,
  Square2StackIcon,
} from "@heroicons/react/24/outline";

interface ContactsToolbarProps {
  search: string;
  type: ContactType | "all";
  balance: "all" | "owed" | "owing";
  view: "list" | "grid";
  onChange: (next: {
    search?: string;
    type?: ContactType | "all";
    balance?: "all" | "owed" | "owing";
    view?: "list" | "grid";
  }) => void;
  onNewContact?: () => void;
}

/**
 * Contacts Toolbar
 *
 * Top toolbar for the contacts index page with:
 * - Search input
 * - Type filter chips (All/Resident/Supplier/Customer)
 * - Balance filter (All/Owed/Owing)
 * - List/Grid toggle
 *
 * Shared by both ContactListView and ContactGridView render modes.
 *
 * Validates: Requirements 1.1, 1.4
 */
export const ContactsToolbar: React.FC<ContactsToolbarProps> = ({
  search,
  type,
  balance,
  view,
  onChange,
  onNewContact,
}) => {
  const typeOptions: Array<{ value: ContactType | "all"; label: string }> = [
    { value: "all", label: "All" },
    { value: "resident", label: "Resident" },
    { value: "supplier", label: "Supplier" },
    { value: "customer", label: "Customer" },
  ];

  const balanceOptions: Array<{
    value: "all" | "owed" | "owing";
    label: string;
  }> = [
    { value: "all", label: "All" },
    { value: "owed", label: "Owed" },
    { value: "owing", label: "Owing" },
  ];

  return (
    <div className="bg-white shadow sm:rounded-lg p-4 space-y-4">
      {/* Search input */}
      <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-md px-3 py-2 focus-within:ring-2 focus-within:ring-blue-500">
        <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search by name, email, or phone…"
          value={search}
          onChange={(e) => onChange({ search: e.target.value })}
          className="flex-1 bg-transparent border-0 outline-none text-sm placeholder-gray-400"
        />
      </div>

      {/* Filters and toggle */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        {/* Type filter chips */}
        <div className="flex flex-wrap gap-2">
          {typeOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange({ type: opt.value })}
              className={`px-3 py-1.5 text-sm font-medium rounded-full transition-colors ${
                type === opt.value
                  ? "bg-gray-900 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Balance filter dropdown */}
        <select
          value={balance}
          onChange={(e) =>
            onChange({ balance: e.target.value as "all" | "owed" | "owing" })
          }
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
        >
          {balanceOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        {/* List/Grid toggle */}
        <div className="flex gap-2 ml-auto">
          <button
            type="button"
            onClick={() => onChange({ view: "list" })}
            className={`p-2 rounded-md transition-colors ${
              view === "list"
                ? "bg-blue-100 text-blue-600"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
            title="List view"
          >
            <Bars3BottomLeftIcon className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => onChange({ view: "grid" })}
            className={`p-2 rounded-md transition-colors ${
              view === "grid"
                ? "bg-blue-100 text-blue-600"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
            title="Grid view"
          >
            <Square2StackIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};
